import { z } from "zod";

const DatabaseTimestampSchema = z.string().datetime({ offset: true });

export const OrganizationRoleSchema = z.enum(["professional", "organization_admin", "content_editor", "support_admin"]);
export const SessionStatusSchema = z.enum(["active", "completed", "locked", "deleted"]);
export const StorageModeSchema = z.enum(["temporary", "summary", "pseudonymous"]);

export const HistoryItemSchema = z.object({
  id: z.string().uuid(),
  occurred_at: DatabaseTimestampSchema,
  template_title: z.string().min(1),
  pseudonym: z.string().nullable(),
  themes: z.array(z.string()),
  has_follow_up: z.boolean(),
  expires_at: DatabaseTimestampSchema,
});
export const HistoryListSchema = z.array(HistoryItemSchema);

export const MetricPointSchema = z.object({
  session_id: z.string().uuid(),
  pseudonym: z.string().min(1).max(80),
  occurred_at: DatabaseTimestampSchema,
  metric_key: z.string().min(1),
  metric_label: z.string().min(1),
  metric_value: z.number().int().min(1).max(5),
  metric_direction: z.enum(["higher_is_better", "higher_is_more"]),
});
export const MetricHistorySchema = z.array(MetricPointSchema);

export const ActiveFollowUpActionSchema = z.object({
  id: z.string().uuid(), session_id: z.string().uuid(), pseudonym: z.string().min(1).max(80),
  description: z.string(), responsible: z.string().nullable(), start_date: z.string().date().nullable(), follow_up_date: z.string().date().nullable(),
  hoped_outcome: z.string().nullable(), success_indicator: z.string().nullable(), status: z.enum(["planned", "in_progress"]), visibility: z.enum(["shared", "professional"]),
});
export const ActiveFollowUpActionsSchema = z.array(ActiveFollowUpActionSchema);

export const StoredSummaryItemSchema = z.object({
  questionId: z.string().min(1),
  question: z.string().min(1),
  topicLabel: z.string().min(1).optional(),
  answer: z.string(),
  theme: z.string().min(1),
  category: z.string().min(1),
  participantPriority: z.number().int().min(1).max(5).nullable().optional(),
  metricKey: z.string().min(1).optional(),
  metricLabel: z.string().min(1).optional(),
  metricValue: z.number().int().min(1).max(5).optional(),
  metricDirection: z.enum(["higher_is_better", "higher_is_more"]).optional(),
});

export const HistoryDetailSchema = z.object({
  version: z.number().int().positive(),
  approvedAt: DatabaseTimestampSchema,
  items: z.array(StoredSummaryItemSchema),
  actions: z.array(z.object({
    id: z.string().uuid(),
    description: z.string().min(1),
    responsible: z.string().nullable(),
    startDate: z.string().date().nullable(),
    followUpDate: z.string().date().nullable(),
    hopedOutcome: z.string().nullable(),
    successIndicator: z.string().nullable(),
    status: z.enum(["planned", "in_progress", "done", "cancelled"]),
    visibility: z.enum(["shared", "professional"]),
  })),
});

export const SharedSummaryPayloadSchema = z.object({
  sessionId: z.string().uuid(),
  version: z.number().int().positive(),
  items: z.array(z.object({ questionId: z.string().min(1), question: z.string().min(1), answer: z.string(), theme: z.string().min(1), category: z.string().min(1) })),
  approvedAt: DatabaseTimestampSchema,
});

export const SaveApprovedSessionInputSchema = z.object({
  templateId: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  templateVersion: z.number().int().positive(),
  pseudonym: z.string().trim().min(1).max(80).nullable().default(null),
  themes: z.array(z.string().trim().min(1).max(100)).max(30),
  items: z.array(z.object({
    questionId: z.string().min(1).max(160),
    question: z.string().min(1).max(500),
    topicLabel: z.string().min(1).max(160).optional(),
    answer: z.string().max(5000),
    theme: z.string().min(1).max(100),
    category: z.string().min(1).max(100),
    participantPriority: z.number().int().min(1).max(5).nullable().optional(),
    metricKey: z.string().min(1).max(100).optional(),
    metricLabel: z.string().min(1).max(160).optional(),
    metricValue: z.number().int().min(1).max(5).optional(),
    metricDirection: z.enum(["higher_is_better", "higher_is_more"]).optional(),
  })).min(1),
  actions: z.array(z.object({
    description: z.string().trim().min(1).max(1000),
    responsible: z.string().trim().max(160),
    startDate: z.string().date().or(z.literal("")),
    followUpDate: z.string().date().or(z.literal("")),
    hopedOutcome: z.string().trim().max(1000),
    successIndicator: z.string().trim().max(1000),
    status: z.enum(["planned", "in_progress", "done", "cancelled"]),
    visibility: z.enum(["shared", "professional"]),
  })).max(50),
});

export type OrganizationRole = z.infer<typeof OrganizationRoleSchema>;
export type HistoryItem = z.infer<typeof HistoryItemSchema>;
export type HistoryDetail = z.infer<typeof HistoryDetailSchema>;
export type MetricPoint = z.infer<typeof MetricPointSchema>;
export type ActiveFollowUpAction = z.infer<typeof ActiveFollowUpActionSchema>;
export type SharedSummaryPayload = z.infer<typeof SharedSummaryPayloadSchema>;
export type SaveApprovedSessionInput = z.infer<typeof SaveApprovedSessionInputSchema>;

export interface SamklangDatabase {
  public: {
    Tables: {
      profiles: { Row: { id: string; display_name: string | null; created_at: string; updated_at: string }; Insert: { id: string; display_name?: string | null }; Update: { display_name?: string | null; updated_at?: string }; Relationships: [] };
      organizations: { Row: { id: string; name: string; slug: string; retention_days: number; created_at: string }; Insert: { name: string; slug: string; retention_days?: number }; Update: { name?: string; retention_days?: number }; Relationships: [] };
      organization_members: { Row: { organization_id: string; user_id: string; role: OrganizationRole; created_at: string }; Insert: { organization_id: string; user_id: string; role?: OrganizationRole }; Update: { role?: OrganizationRole }; Relationships: [] };
      conversation_template_versions: { Row: { template_id: string; version: number; status: "draft" | "review" | "published" | "retired"; content: unknown; content_hash: string; reviewed_at: string | null; published_at: string | null; created_at: string }; Insert: { template_id: string; version: number; status: "draft" | "review" | "published" | "retired"; content: unknown; content_hash: string }; Update: { status?: "draft" | "review" | "published" | "retired"; content?: unknown; content_hash?: string }; Relationships: [] };
      sessions: { Row: { id: string; organization_id: string; created_by: string; template_id: string; template_version: number; storage_mode: string; status: string; pseudonym: string | null; themes: string[]; occurred_at: string; expires_at: string; created_at: string; updated_at: string }; Insert: { id?: string; organization_id: string; created_by: string; template_id: string; template_version: number; storage_mode: string; status?: string; pseudonym?: string | null; themes?: string[]; occurred_at?: string; expires_at: string }; Update: { status?: string; pseudonym?: string | null; themes?: string[]; updated_at?: string }; Relationships: [] };
      session_summaries: { Row: { id: string; session_id: string; organization_id: string; created_by: string; version: number; summary: unknown; approved_at: string; created_at: string }; Insert: { session_id: string; organization_id: string; created_by: string; version: number; summary: unknown; approved_at: string }; Update: { summary?: unknown; version?: number; approved_at?: string }; Relationships: [] };
      follow_up_actions: { Row: { id: string; session_id: string; organization_id: string; created_by: string; description: string; responsible: string | null; start_date: string | null; follow_up_date: string | null; hoped_outcome: string | null; success_indicator: string | null; follow_up_result: string | null; resolution: string | null; status: string; visibility: string; created_at: string }; Insert: { session_id: string; organization_id: string; created_by: string; description: string; responsible?: string | null; start_date?: string | null; follow_up_date?: string | null; hoped_outcome?: string | null; success_indicator?: string | null; status?: string; visibility?: string }; Update: { description?: string; responsible?: string | null; start_date?: string | null; follow_up_date?: string | null; hoped_outcome?: string | null; success_indicator?: string | null; follow_up_result?: string | null; resolution?: string | null; status?: string; visibility?: string }; Relationships: [] };
    };
    Views: {
      history_items: { Row: { id: string; occurred_at: string; template_title: string; pseudonym: string | null; themes: string[]; has_follow_up: boolean; expires_at: string }; Relationships: [] };
      metric_history: { Row: { session_id: string; organization_id: string; created_by: string; pseudonym: string; occurred_at: string; question_id: string; metric_key: string; metric_label: string; metric_value: number; metric_direction: string }; Relationships: [] };
      active_follow_up_actions: { Row: { id: string; session_id: string; organization_id: string; created_by: string; pseudonym: string; description: string; responsible: string | null; start_date: string | null; follow_up_date: string | null; hoped_outcome: string | null; success_indicator: string | null; status: "planned" | "in_progress"; visibility: "shared" | "professional"; created_at: string }; Relationships: [] };
    };
    Functions: Record<string, never>;
    Enums: Record<string, never>;
    CompositeTypes: Record<string, never>;
  };
}
